import { create } from 'zustand';
import type { Patient, PatientStatus } from '@/models/patient';
import { readPatientData } from '@/services/firestore/patients';

type ListState = {
  list: Patient[];
  isLoading: boolean;
  refresh: (uid: string) => Promise<void>;
  addLocal: (p: Patient) => void;
  removeLocal: (id: string) => void;
  replaceLocal: (p: Patient) => void;
};

const without = (list: Patient[], id: string) => list.filter((p) => p.id !== id);

function makeListStore(status: PatientStatus) {
  return create<ListState>((set) => ({
    list: [],
    isLoading: true,
    refresh: async (uid) => {
      set({ isLoading: true });
      try {
        const list = await readPatientData({ uid, status });
        set({ list, isLoading: false });
      } catch (e) {
        // Without this, a transient Firestore error (offline, rules check
        // race) leaves isLoading stuck on `true` and the page shows a
        // never-ending spinner. Resolve the flag either way; caller can
        // surface the error separately if it wants.
        console.warn('patient refresh failed:', e);
        set({ isLoading: false });
      }
    },
    addLocal: (p) => set((s) => ({ list: [p, ...without(s.list, p.id)] })),
    removeLocal: (id) => set((s) => ({ list: without(s.list, id) })),
    replaceLocal: (p) =>
      set((s) => ({ list: s.list.map((x) => (x.id === p.id ? p : x)) })),
  }));
}

export const useActivePatients = makeListStore('active');
export const useOrientedPatients = makeListStore('oriented');
